// Settings, Renewal sheets (Manager and Admin): upload the yearly renewal workbooks, read the dry run
// counts in plain words, and confirm the close. Replaces the Mac catch-up (sheet:check, sheet:run)
// until the VAs move to the portal. Markup from the kit: the drop area (`drop`, `drop-ic`), the file
// row (`imp-file`), the Settings cards (`icard`, `ih`, `esub`), the calm note (`anote calm`), the
// stat numbers (`snum`), the confirm popup and the toast.
import { useRef, useState, type DragEvent, type RefObject } from 'react';
import type { Api, SheetClosed, SheetCounts, SheetPreview } from '../auth/api';
import { ConfirmPopup } from '../shell/ConfirmPopup';
import { FileIcon, InfoIcon, UploadIcon } from '../shell/icons';
import { useOnline } from '../shell/online';
import { Toast } from '../shell/Toast';
import { fileSize } from '../renewals/upload-lines';
import {
  alsoLine,
  closeButton,
  closedToast,
  confirmLine,
  confirmTitle,
  countBoxes,
  pickSheetProblem,
  sheetCopy as c,
  sheetError,
  tabWarnings,
  workbookSub,
  workbookTitle,
} from './sheet-upload-lines';

type Phase = 'pick' | 'reading' | 'preview' | 'closing' | 'done';

export function RenewalSheetsCard({ api }: { api: Api }) {
  const [files, setFiles] = useState<File[]>([]);
  const [phase, setPhase] = useState<Phase>('pick');
  const [preview, setPreview] = useState<SheetPreview | null>(null);
  const [result, setResult] = useState<SheetClosed | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [gone, setGone] = useState(false);
  const [asking, setAsking] = useState(false);
  const [toast, setToast] = useState<{ id: number; text: string } | null>(null);
  const pickRef = useRef<HTMLInputElement>(null);
  const online = useOnline();

  function take(picked: File[]) {
    if (!picked.length) return;
    const next = [...files, ...picked];
    const problem = pickSheetProblem(next);
    setError(problem);
    if (!problem) setFiles(next);
  }

  function reset() {
    setFiles([]);
    setPreview(null);
    setResult(null);
    setError(null);
    setGone(false);
    setAsking(false);
    setPhase('pick');
  }

  async function check() {
    if (!files.length || phase === 'reading') return;
    setPhase('reading');
    setError(null);
    setGone(false);
    const res = await api.sheetPreview(files);
    if (!res.ok) {
      setError(sheetError(res.status, res.error, res.body));
      setPhase('pick');
      return;
    }
    setPreview(res.body);
    setPhase('preview');
  }

  async function close() {
    if (!preview?.previewId || phase === 'closing') return;
    setPhase('closing');
    const res = await api.sheetClose(preview.previewId);
    setAsking(false);
    if (!res.ok) {
      // The preview works once and for 15 minutes; after that, or after a deploy, it is gone.
      if (res.error === 'preview_gone') setGone(true);
      else setError(sheetError(res.status, res.error, res.body));
      setPhase('preview');
      return;
    }
    setResult(res.body);
    setToast({ id: Date.now(), text: closedToast(res.body.closed, res.body.planned) });
    setPhase('done');
  }

  return (
    <div className="col g16">
      {(phase === 'pick' || phase === 'reading') && (
        <PickView
          files={files}
          busy={phase === 'reading'}
          online={online}
          error={error}
          pickRef={pickRef}
          onPick={take}
          onRemove={(i) => {
            setFiles(files.filter((_, k) => k !== i));
            setError(null);
          }}
          onCheck={() => void check()}
        />
      )}
      {(phase === 'preview' || phase === 'closing') && preview && (
        <>
          {gone ? (
            <GoneView onAgain={reset} />
          ) : (
            <PreviewView
              preview={preview}
              busy={phase === 'closing'}
              online={online}
              onClose={() => setAsking(true)}
              onCancel={reset}
            />
          )}
          {error && (
            <p className="ferr m0" role="alert">
              {error}
            </p>
          )}
        </>
      )}
      {phase === 'done' && result && <DoneView result={result} onMore={reset} />}
      {asking && preview && (
        <ConfirmPopup
          title={confirmTitle(preview.closeCount)}
          line={confirmLine}
          action={closeButton(preview.closeCount)}
          busy={phase === 'closing'}
          onConfirm={() => void close()}
          onCancel={() => setAsking(false)}
        />
      )}
      {toast && <Toast key={toast.id} text={toast.text} />}
    </div>
  );
}

/** The upload step: the drop area, the chosen files and Check workbooks. */
export function PickView({
  files,
  busy,
  online = true,
  error,
  pickRef,
  onPick,
  onRemove,
  onCheck,
}: {
  files: { name: string; size: number }[];
  busy: boolean;
  online?: boolean;
  error: string | null;
  pickRef?: RefObject<HTMLInputElement | null>;
  onPick: (files: File[]) => void;
  onRemove: (index: number) => void;
  onCheck: () => void;
}) {
  function onDrop(e: DragEvent) {
    e.preventDefault();
    onPick([...e.dataTransfer.files]);
  }
  return (
    <div className="icard">
      <div className="ih">
        <span className="dot ok" />
        <span className="t14 w6 ink">{c.cardTitle}</span>
        <span className="t12 muted">{c.cardSub}</span>
      </div>
      <p className="esub m0">{c.note}</p>
      {files.map((f, i) => (
        <div key={`${i}-${f.name}`} className="imp-file">
          <FileIcon className="ico s14" />
          <span className="t13 ink ell">{f.name}</span>
          <span className="t12 muted">{fileSize(f.size)}</span>
          <button type="button" className="linkbtn" disabled={busy} onClick={() => onRemove(i)}>
            {c.remove}
          </button>
        </div>
      ))}
      {files.length < 3 && (
        <div className="drop" onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
          <span className="drop-ic">
            <UploadIcon className="ico s14" />
          </span>
          <span className="t13 w5 ink">{c.dropTitle}</span>
          <span className="t12 muted">{c.dropLine}</span>
          <button
            type="button"
            className="btn btn-sm"
            disabled={busy}
            onClick={() => pickRef?.current?.click()}
          >
            {c.choose}
          </button>
        </div>
      )}
      <input
        ref={pickRef}
        type="file"
        multiple
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        hidden
        aria-hidden="true"
        tabIndex={-1}
        onChange={(e) => {
          onPick([...(e.target.files ?? [])]);
          e.target.value = '';
        }}
      />
      {error && (
        <p className="ferr m0" role="alert">
          {error}
        </p>
      )}
      <div className="row g8 wrap">
        <span className="grow" />
        {busy && <span className="t12 muted">{c.checking}</span>}
        <button
          type="button"
          className="btn btn-primary"
          disabled={!files.length || busy || !online}
          onClick={onCheck}
        >
          {busy && <span className="spin" />}
          {c.check}
        </button>
      </div>
    </div>
  );
}

/** One workbook's four numbers and the smaller counts under them. */
function WorkbookCounts({ counts, index }: { counts: SheetCounts; index: number }) {
  const also = alsoLine(counts);
  const warnings = tabWarnings(counts);
  return (
    <div className="icard">
      <div className="ih">
        <span className="dot ok" />
        <span className="t14 w6 ink">{workbookTitle(counts, index)}</span>
        <span className="t12 muted">{workbookSub(counts)}</span>
      </div>
      <div className="stats">
        {countBoxes(counts).map((b) => (
          <div key={b.id} className="icard" role="group" aria-label={b.label}>
            <span className="t12 muted w6">{b.label}</span>
            <span className={b.n === 0 ? 'snum n-zero' : `snum ${b.tone}`}>
              {b.n.toLocaleString('en-US')}
            </span>
            <span className="t12 text2">{b.line}</span>
          </div>
        ))}
      </div>
      {warnings.length > 0 && (
        <div className="anote" role="alert">
          <InfoIcon className="ico s14" />
          <span>{warnings.join(' ')}</span>
        </div>
      )}
      {also && <p className="esub m0">{also}</p>}
    </div>
  );
}

/** The dry run: counts per workbook, then the primary button. */
export function PreviewView({
  preview,
  busy,
  online = true,
  onClose,
  onCancel,
}: {
  preview: SheetPreview;
  busy: boolean;
  online?: boolean;
  onClose: () => void;
  onCancel: () => void;
}) {
  const n = preview.closeCount;
  return (
    <>
      {preview.counts.map((counts, i) => (
        <WorkbookCounts key={i} counts={counts} index={i} />
      ))}
      <div className="row g8 wrap">
        <span className="t12 muted ell">{n > 0 ? c.kept : c.nothing}</span>
        <span className="grow" />
        <button type="button" className="btn" disabled={busy} onClick={onCancel}>
          {c.cancel}
        </button>
        {n > 0 && (
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !online}
            onClick={onClose}
          >
            {busy && <span className="spin" />}
            {closeButton(n)}
          </button>
        )}
      </div>
    </>
  );
}

/** The 404 on Close: the check expired, was used, or the portal was updated in between. */
export function GoneView({ onAgain }: { onAgain: () => void }) {
  return (
    <div className="anote calm" role="alert">
      <InfoIcon className="ico s14" />
      <span>{c.expired}</span>
      <button type="button" className="btn btn-primary ml-auto" onClick={onAgain}>
        {c.uploadAgain}
      </button>
    </div>
  );
}

function DoneView({ result, onMore }: { result: SheetClosed; onMore: () => void }) {
  return (
    <>
      <div className="anote calm" role="status">
        <InfoIcon className="ico s14" />
        <span>{closedToast(result.closed, result.planned)}</span>
        <button type="button" className="btn ml-auto" onClick={onMore}>
          {c.uploadMore}
        </button>
      </div>
    </>
  );
}
